import { useCallback, useEffect, useState } from 'react';
import { ErrorApi, obtener } from '../api/cliente';

type Estado<T> = {
  datos: T | null;
  cargando: boolean;
  error: string | null;
  recargar: () => void;
};

/**
 * Carga de sólo lectura con cancelación. Se prefirió esto a traer una librería de datos:
 * las pantallas piden un recurso, lo muestran y lo recargan cuando algo cambia, y para eso
 * una librería de caché agrega más conceptos de los que ahorra.
 */
export function useDatos<T>(ruta: string | null, dependencias: unknown[] = []): Estado<T> {
  const [datos, setDatos] = useState<T | null>(null);
  const [cargando, setCargando] = useState(ruta != null);
  const [error, setError] = useState<string | null>(null);
  const [intento, setIntento] = useState(0);

  const recargar = useCallback(() => setIntento((n) => n + 1), []);

  useEffect(() => {
    if (ruta == null) {
      setDatos(null);
      setCargando(false);

      return;
    }

    const control = new AbortController();
    setCargando(true);
    setError(null);

    obtener<T>(ruta, control.signal)
      .then((respuesta) => {
        if (!control.signal.aborted) {
          setDatos(respuesta);
        }
      })
      .catch((fallo: unknown) => {
        if (control.signal.aborted) {
          return;
        }

        setDatos(null);
        setError(
          fallo instanceof ErrorApi
            ? fallo.message
            : 'No se pudo conectar con el servidor. Revisá la conexión.',
        );
      })
      .finally(() => {
        if (!control.signal.aborted) {
          setCargando(false);
        }
      });

    return () => control.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruta, intento, ...dependencias]);

  return { datos, cargando, error, recargar };
}

/**
 * Ejecuta una escritura y expone su estado. Devuelve el mensaje del servidor tal cual: el
 * dominio ya explica por qué rechazó la operación, y reescribirlo acá sólo lo empeora.
 */
export function useAccion() {
  const [ejecutando, setEjecutando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [porCampo, setPorCampo] = useState<Record<string, string[]>>({});

  const correr = useCallback(async <T,>(accion: () => Promise<T>): Promise<T | null> => {
    setEjecutando(true);
    setError(null);
    setPorCampo({});

    try {
      return await accion();
    } catch (fallo: unknown) {
      if (fallo instanceof ErrorApi) {
        setError(fallo.message);
        setPorCampo(fallo.porCampo);
      } else {
        setError('No se pudo conectar con el servidor.');
      }

      return null;
    } finally {
      setEjecutando(false);
    }
  }, []);

  return { correr, ejecutando, error, porCampo, limpiar: () => setError(null) };
}

/** Retrasa un valor: la búsqueda instantánea no dispara una consulta por tecla. */
export function useRetraso<T>(valor: T, ms = 250): T {
  const [retrasado, setRetrasado] = useState(valor);

  useEffect(() => {
    const temporizador = setTimeout(() => setRetrasado(valor), ms);

    return () => clearTimeout(temporizador);
  }, [valor, ms]);

  return retrasado;
}
